import { TBorderWeight, BORDER_WEIGHTS } from './TableNode';
import { normalizeBorderStyle } from './normalizeBorderStyle';

export const BORDER_EDGES = ['top', 'right', 'bottom', 'left'] as const;
export type TBorderEdge = (typeof BORDER_EDGES)[number];

/**
 * Returns TipTap attribute definitions for per-edge border styles.
 * Shared between CustomTableCell and CustomTableHeader.
 */
export const borderEdgeStyleAttributes = () => {
  const attrs: Record<string, object> = {};

  for (const edge of BORDER_EDGES) {
    const dataAttr = `data-border-${edge}-style`;
    const attrKey = `border${capitalize(edge)}Style`;

    attrs[attrKey] = {
      default: null,
      parseHTML: (element: HTMLElement) => normalizeBorderStyle(element.getAttribute(dataAttr)),
      renderHTML: (attributes: Record<string, unknown>) => {
        const value = attributes[attrKey];
        if (!value) return {};
        return { [dataAttr]: value };
      },
    };
  }

  return attrs;
};

export const offsetCellAttribute = () => ({
  offsetCell: {
    default: false,
    parseHTML: (element: HTMLElement) => element.getAttribute('data-offset-cell') === 'true',
    renderHTML: (attributes: Record<string, unknown>) => (attributes.offsetCell ? { 'data-offset-cell': 'true' } : {}),
  },
});

/**
 * Returns TipTap attribute definitions for per-edge border weights.
 * Shared between CustomTableCell and CustomTableHeader.
 */
export const borderEdgeWeightAttributes = () => buildEdgeAttributes<TBorderWeight>('weight', 'Weight', BORDER_WEIGHTS);

/** Generic factory for per-edge border attributes (style or weight). */
const buildEdgeAttributes = <T extends string>(kind: string, kindCap: string, allowedValues: readonly T[]) => {
  const attrs: Record<string, object> = {};

  for (const edge of BORDER_EDGES) {
    const dataAttr = `data-border-${edge}-${kind}`;
    const attrKey = `border${capitalize(edge)}${kindCap}`;

    attrs[attrKey] = {
      default: null,
      parseHTML: (element: HTMLElement) => {
        const value = element.getAttribute(dataAttr);
        if (value && (allowedValues as readonly string[]).includes(value)) return value;
        return null;
      },
      renderHTML: (attributes: Record<string, unknown>) => {
        const value = attributes[attrKey];
        if (!value) return {};
        return { [dataAttr]: value };
      },
    };
  }

  return attrs;
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
